import {
    CHECK_IN_PROGRESS,
    DOMAIN_URL_SITEMAP_KEYS,
    EVENTS,
    SCAN_IN_PROGRESS_STATUSES,
    SCAN_JURISDICTIONS,
    SCAN_REQUIREMENTS,
    SCAN_REQUIREMENT_MAPPING,
    SCAN_STATUSES,
} from '/b2b/domains/constants';
import {
    createDomainScan,
    fetchCmpConfigByDomains,
    fetchDomain,
    fetchDomainScans,
} from '/b2b/domains/services';
import { findLastRelevantScan, isScanWithinFrequencyLimit } from '/b2b/domains/customerPlan';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router';

import Poll from '/b2b/common/helpers/Poll';
import analytics from '/b2b/common/analytics';
import { exponentialBackoff } from '/b2b/common/helpers/Backoff';
import get from 'lodash/get';
import { getScanResultStatus } from './DomainDetails.status';
import { hasWhyItMatters } from '/b2b/domains/components/WhyItMatters';
import moment from 'moment';
import useScanDetail from './hooks/useScanDetail';

export const SCAN_ID_PARAM_KEY = 'id';

const SCAN_CHANGE_DELAY = 500;

/**
 * A scan can take anywhere from a couple of minutes to about an hour when the scanner is
 * backlogged, so the status check starts responsive and eases off rather than holding a fixed
 * rate for the whole run: 10s, 20s, 40s, then every 60s. A scan that finishes early is still
 * picked up quickly, while an hour-long one costs ~60 requests instead of ~360.
 */
const SCAN_POLL_INTERVAL_MS = 10000;
const SCAN_POLL_MAX_INTERVAL_MS = 60000;

/**
 * Poll's default request timeout (10s) is tight for the scan endpoint, and Poll stops for good
 * once a request times out or rejects - which leaves the in-progress indicator frozen. Give
 * requests room, and rebuild the Poll a bounded number of times when one still fails, backing
 * off each time so a struggling endpoint is not hammered (10s, 20s, ... capped at 5m).
 */
const SCAN_POLL_TIMEOUT_MS = 30000;
const SCAN_POLL_MAX_RECOVERY_INTERVAL_MS = 300000;
const SCAN_POLL_MAX_RECOVERIES = 6;

/**
 * Only treat a still-running scan as abandoned well past the worst expected runtime. A threshold
 * inside the normal range makes the indicator switch itself off while the scan is still going.
 */
const SCAN_STALE_AFTER_MINUTES = 120;

const FREQUENCY_MAP = {
    0: 'none',
    1: 'daily',
    7: 'weekly',
    30: 'monthly',
    90: 'quarterly',
};

const DEFAULT_SCAN_INTERVAL_DAYS = 7;

const defaultState = {
    dismissedAlertScanIds: new Set(),
    details: {},
    latestScan: {},
    loading: true,
    scanChanging: false,
    scanInProgress: false,
    scans: [],
};

// A running scan is listed while it reports, then replaced by the finished one, so match by id.
const upsertScan = (scans, scan) =>
    scans.some(({ id }) => id === scan.id)
        ? scans.map(existing => (existing.id === scan.id ? scan : existing))
        : [scan, ...scans];

export const hasRejectAllData = result => {
    const jurisdiction = result?.jurisdictions?.[SCAN_JURISDICTIONS.CALIFORNIA] || {};
    if (jurisdiction.status === 'error') return true;
    const trackerAnalysis = jurisdiction.fields?.trackerAnalysis;
    if (!trackerAnalysis) return false;
    const hasCategories =
        Object.keys(trackerAnalysis.removedByCategory || {}).length > 0 ||
        Object.keys(trackerAnalysis.remainingByCategory || {}).length > 0;
    const hasTrackerCounts =
        trackerAnalysis.matchesOnInitialLoad !== undefined ||
        trackerAnalysis.matchesAfterRejectAll !== undefined;
    return hasCategories || hasTrackerCounts;
};

export const hasFirstLoadData = result => {
    const jurisdiction = result?.jurisdictions?.[SCAN_JURISDICTIONS.CALIFORNIA] || {};
    if (jurisdiction.status === 'error') return true;
    const trackerAnalysis = jurisdiction.fields?.trackerAnalysis;
    return Boolean(trackerAnalysis);
};

export const isCheckInProgress = result => result?.status === CHECK_IN_PROGRESS;

/**
 * Whether a check's row expands into a detail panel. Reject-all and first-load bring their own
 * results grids; the rest need something to show — fields, a screenshot, an error, or at minimum the
 * approved explanation of why the check exists, so a passing check that surfaced no fields still
 * expands (CCM-586).
 */
export const hasScanResultDetail = result => {
    const { requirement } = result || {};
    const jurisdiction = result?.jurisdictions?.[SCAN_JURISDICTIONS.CALIFORNIA] || {};
    if (result?.isPlaceholder || isCheckInProgress(jurisdiction)) {
        return false;
    }

    if (
        requirement === SCAN_REQUIREMENTS.REJECT_ALL ||
        requirement === SCAN_REQUIREMENTS.FIRST_LOAD ||
        jurisdiction.status === 'error'
    ) {
        return true;
    }

    const { imageUrl, ...fields } = jurisdiction.fields || {};
    if (imageUrl) return true;

    const fieldPaths = SCAN_REQUIREMENT_MAPPING[requirement]?.fieldPaths || {};
    if (Object.values(fieldPaths).some(fieldPath => get(fields, fieldPath))) return true;

    return hasWhyItMatters(requirement);
};

export const getEnabledResults = (results, ccRejectAllEnabled, ccFirstLoadEnabled) =>
    (results || []).filter(r => {
        if (r.requirement === SCAN_REQUIREMENTS.REJECT_ALL) {
            return ccRejectAllEnabled && (isCheckInProgress(r) || hasRejectAllData(r));
        }

        if (r.requirement === SCAN_REQUIREMENTS.FIRST_LOAD) {
            return ccFirstLoadEnabled && (isCheckInProgress(r) || hasFirstLoadData(r));
        }

        return true;
    });

const useDomainDetails = props => {
    const {
        ccFirstLoadEnabled,
        ccRejectAllEnabled,
        createSuccessSnack,
        createErrorSnack,
        createWarningSnack,
        domain,
    } = props;

    const history = useHistory();
    const location = useLocation();

    // Extract scan id from URL query params
    const scanIdParam = useMemo(() => {
        const queryParams = new URLSearchParams(location.search);
        const scanId = queryParams.get(SCAN_ID_PARAM_KEY);
        try {
            return parseInt(scanId, 10);
        } catch {
            return null;
        }
    }, [location.search]);

    // Read through a ref rather than closing over `location.search` so that `setScanIdParam`
    // stays referentially stable: it feeds `pollForScanStatus`, which the details fetch effect
    // depends on, so a changing identity refetches the domain and restarts the poll on every
    // URL change - including the ones this hook makes itself.
    const locationSearchRef = useRef(location.search);
    locationSearchRef.current = location.search;

    // Update the scan id in the URL query parameter
    const setScanIdParam = useCallback(
        scanId => {
            const searchParams = new URLSearchParams(locationSearchRef.current);
            searchParams.set(SCAN_ID_PARAM_KEY, scanId);
            history.replace({ search: searchParams.toString() });
        },
        [history]
    );

    // Use factory function to ensure each component instance gets its own Set
    const [state, setState] = useState(() => ({
        ...defaultState,
        dismissedAlertScanIds: new Set(),
    }));
    const {
        dismissedAlertScanIds,
        details,
        latestScan,
        loading,
        scanChanging,
        scanInProgress,
        scans,
        configId,
    } = state;

    const updateState = useCallback(
        newState => setState(prevState => ({ ...prevState, ...newState })),
        []
    );

    // Derive selectedScan from URL param and scans list
    const selectedScan = useMemo(() => {
        if (!scans.length) {
            return null;
        }

        // If scanId param exists, find that scan
        if (scanIdParam) {
            const matchingScan = scans.find(scan => scan.id === scanIdParam);
            if (matchingScan) {
                return matchingScan;
            }
        }

        // Default to latest scan (first in list)
        return scans[0];
    }, [scans, scanIdParam]);

    const scanStatusPoll = useRef(null);
    const scanStatusPollRestart = useRef(null);
    const scanStatusPollRecoveries = useRef(0);
    // Lets the recovery path re-enter pollForScanStatus without making it self-referential
    const pollForScanStatusRef = useRef(null);
    const scanChangeTimeout = useRef(null);
    const pendingScanId = useRef(null);
    const autoSelectedScanIds = useRef(new Set());

    // Widens the gap between status checks as a scan runs on. A scan still running is not news, so
    // this keeps growing across successful responses and only drops back when a poll is built fresh
    // (page load, or a scan the user just triggered) rather than resumed after a failure.
    const scanPollBackoff = useMemo(
        () =>
            exponentialBackoff({
                initial: SCAN_POLL_INTERVAL_MS,
                max: SCAN_POLL_MAX_INTERVAL_MS,
            }),
        []
    );
    // Widens the gap between retries as failures pile up; any successful response resets it
    const scanRecoveryBackoff = useMemo(
        () =>
            exponentialBackoff({
                initial: SCAN_POLL_INTERVAL_MS,
                max: SCAN_POLL_MAX_RECOVERY_INTERVAL_MS,
            }),
        []
    );

    useEffect(
        () => () => {
            scanStatusPoll.current && scanStatusPoll.current.abort();
            scanStatusPoll.current = null;
            scanStatusPollRestart.current && clearTimeout(scanStatusPollRestart.current);
            scanStatusPollRestart.current = null;
            scanChangeTimeout.current && clearTimeout(scanChangeTimeout.current);
            scanChangeTimeout.current = null;
            pendingScanId.current = null;
        },
        []
    );

    const pollForScanStatus = useCallback(
        (domain, scans = [], { resumeBackoff = false } = {}) => {
            scanStatusPoll.current && scanStatusPoll.current.abort();
            scanStatusPoll.current = null;
            scanStatusPollRestart.current && clearTimeout(scanStatusPollRestart.current);
            scanStatusPollRestart.current = null;

            // A rebuild after a failure carries on where the previous one left off; anything else
            // (page load, a scan the user just triggered) starts checking at the fast cadence
            if (!resumeBackoff) {
                scanPollBackoff.reset();
            }

            scanStatusPoll.current = Poll(
                () =>
                    fetchDomainScans({
                        domain,
                        limit: 1,
                        skip: 0,
                    }),
                {
                    immediate: false,
                    interval: scanPollBackoff,
                    timeout: SCAN_POLL_TIMEOUT_MS,
                }
            )
                .then(response => {
                    // Any response means the API is healthy again, so allow recoveries afresh
                    scanStatusPollRecoveries.current = 0;
                    scanRecoveryBackoff.reset();
                    return response;
                })
                .change(({ results: latestScans = [] } = {}) => {
                    const latestScan = latestScans[0];

                    if (!latestScan) {
                        updateState({ loading: false, scanInProgress: false });
                        throw new Error('No latest scan');
                    }

                    const { created, id, startedAt = created, status } = latestScan;

                    const latestScanInProgress = [
                        SCAN_STATUSES.PENDING,
                        SCAN_STATUSES.RUNNING,
                    ].includes(status);

                    const scanIsStale =
                        moment().diff(moment(startedAt), 'minutes') >= SCAN_STALE_AFTER_MINUTES;

                    if (latestScanInProgress && !scanIsStale) {
                        // The captured `scans` is stale and would re-add the same scan every tick.
                        setState(prevState => ({
                            ...prevState,
                            latestScan,
                            loading: false,
                            scanInProgress: true,
                            scans: upsertScan(prevState.scans, latestScan),
                        }));

                        // Only once it has data, or a full report is replaced by an empty table.
                        if (latestScan.results?.length && !autoSelectedScanIds.current.has(id)) {
                            autoSelectedScanIds.current.add(id);
                            setScanIdParam(id);
                        }
                        return;
                    }

                    const newScan = !scans.find(scan => scan.id === id);
                    const newlyCompletedScan = status === SCAN_STATUSES.COMPLETED && newScan;

                    // Update URL if a newly completed scan is being selected
                    if (newlyCompletedScan) {
                        setScanIdParam(latestScan.id);
                    }

                    // Replaces the in-progress copy this poll added with the finished scan.
                    setState(prevState => ({
                        ...prevState,
                        latestScan,
                        loading: false,
                        scanInProgress: false,
                        ...(status === SCAN_STATUSES.COMPLETED && {
                            scans: upsertScan(prevState.scans, latestScan),
                        }),
                    }));

                    throw new Error('No scan in progress');
                })
                .catch(error => {
                    const { message } = error || {};
                    if (message === 'Aborted') {
                        // Torn down on purpose (unmount, or restarted for a newly created scan)
                        return;
                    }
                    if (scanStatusPollRecoveries.current >= SCAN_POLL_MAX_RECOVERIES) {
                        updateState({ scanInProgress: false });
                        return;
                    }
                    // Poll has stopped itself after a timeout or a failed request. Build a new one
                    // so the indicator keeps tracking the scan instead of freezing on-screen.
                    scanStatusPollRecoveries.current += 1;
                    scanStatusPollRestart.current = setTimeout(() => {
                        scanStatusPollRestart.current = null;
                        pollForScanStatusRef.current?.(domain, scans, { resumeBackoff: true });
                    }, scanRecoveryBackoff());
                })
                .start();
        },
        [scanPollBackoff, scanRecoveryBackoff, setScanIdParam, setState, updateState]
    );

    useEffect(() => {
        pollForScanStatusRef.current = pollForScanStatus;
    }, [pollForScanStatus]);

    useEffect(() => {
        Promise.all([
            fetchDomain(domain),
            fetchDomainScans({
                domain,
                limit: 25,
                skip: 0,
                // eslint-disable-next-line camelcase
                sort_by: 'completed_at',
                // eslint-disable-next-line camelcase
                sort_order: 'desc',
                // Includes pending because CustomerAnalysis.status never reaches running on the
                // normal path — only retry and the bulk paths set it — so filtering to running
                // would exclude a scan for its entire life. Failed and cancelled have nothing to
                // select and surface through the scanFailed alert instead.
                status: `${SCAN_STATUSES.COMPLETED},${SCAN_STATUSES.RUNNING},${SCAN_STATUSES.PENDING}`,
            }),
        ])
            .then(([details, { results: scans = [] } = {}]) => {
                updateState({
                    details,
                    loading: false,
                    scans,
                });
                pollForScanStatus(domain, scans);
            })
            .catch(error => updateState({ error, loading: false }));

        fetchCmpConfigByDomains([domain])
            .then(config => {
                updateState({ configId: config?.configId || null });
            })
            .catch(() => {
                updateState({ configId: null });
            });
    }, [domain, updateState, pollForScanStatus]);

    // Set URL param to latest scan if no param exists
    useEffect(() => {
        if (!loading && scans.length > 0 && !scanIdParam) {
            setScanIdParam(scans[0].id);
        }
    }, [loading, scans, scanIdParam, setScanIdParam]);

    const handleStartScan = useCallback(() => {
        const sitemap = Object.entries(DOMAIN_URL_SITEMAP_KEYS).reduce((acc, [type, key]) => {
            const urls = (details?.urls || [])
                .filter(domainUrl => domainUrl.type === type)
                .map(domainUrl => domainUrl.url);
            if (urls?.length) {
                acc[key] = urls;
            }
            return acc;
        }, {});

        analytics.track(EVENTS.TRIGGER_DOMAIN_SCAN);
        updateState({ scanInProgress: true });
        return createDomainScan(domain, sitemap)
            .then(({ id, status } = {}) => {
                const newScanStarted =
                    [SCAN_STATUSES.PENDING, SCAN_STATUSES.RUNNING].includes(status) &&
                    !scans.find(scan => scan.id === id);
                if (newScanStarted) {
                    pollForScanStatus(domain, scans);
                    return createSuccessSnack('domains.siteScan.toasts.trigger.success');
                }

                updateState({ scanInProgress: false });

                return createWarningSnack('domains.siteScan.toasts.trigger.warning');
            })
            .catch(() => {
                updateState({ scanInProgress: false });
                return createErrorSnack('domains.siteScan.toasts.trigger.error');
            });
    }, [
        createSuccessSnack,
        createErrorSnack,
        createWarningSnack,
        domain,
        details,
        pollForScanStatus,
        scans,
        updateState,
    ]);

    const handleScanChange = useCallback(
        event => {
            const targetScanId = event.target.value;

            // Clear any pending timeout
            scanChangeTimeout.current && clearTimeout(scanChangeTimeout.current);
            pendingScanId.current = targetScanId;

            updateState({ scanChanging: true });

            scanChangeTimeout.current = setTimeout(() => {
                setScanIdParam(pendingScanId.current);
                pendingScanId.current = null;
                updateState({ scanChanging: false });
            }, SCAN_CHANGE_DELAY);
        },
        [updateState, setScanIdParam]
    );

    const handleDismissAlert = useCallback(() => {
        if (!selectedScan?.id) {
            return;
        }
        setState(prevState => ({
            ...prevState,
            dismissedAlertScanIds: new Set([...prevState.dismissedAlertScanIds, selectedScan.id]),
        }));
    }, [selectedScan]);

    const updateDetails = useCallback(
        newDetails => updateState({ details: newDetails }),
        [updateState]
    );

    const { scan: scanDetail, isLoading: scanDetailLoading } = useScanDetail(selectedScan?.id);

    const selectedScanResults = useMemo(
        () => getEnabledResults(selectedScan?.results, ccRejectAllEnabled, ccFirstLoadEnabled),
        [selectedScan?.results, ccRejectAllEnabled, ccFirstLoadEnabled]
    );

    const latestScanResults = useMemo(
        () => getEnabledResults(latestScan?.results, ccRejectAllEnabled, ccFirstLoadEnabled),
        [latestScan?.results, ccRejectAllEnabled, ccFirstLoadEnabled]
    );

    // In-progress checks count toward the total but not failures
    const reportedSelectedResults = selectedScanResults.filter(
        result => !isCheckInProgress(result)
    );
    const reportedLatestResults = latestScanResults.filter(result => !isCheckInProgress(result));

    const allResultsCacheHit = useMemo(
        () =>
            reportedSelectedResults.length > 0 &&
            reportedSelectedResults.length === selectedScanResults.length &&
            reportedSelectedResults.every(result => result.cacheHit),
        [reportedSelectedResults, selectedScanResults.length]
    );

    const awaitingFirstResults =
        SCAN_IN_PROGRESS_STATUSES.includes(selectedScan?.status) &&
        selectedScanResults.length === 0;

    const selectedScanHasPendingChecks =
        selectedScanResults.length !== reportedSelectedResults.length;

    // Keep the summary aligned with the status chip rendered for each grid row (CCM-577).
    const selectedStatusCounts = selectedScanResults.reduce((counts, result) => {
        const status = getScanResultStatus(result);
        counts[status] = (counts[status] || 0) + 1;
        return counts;
    }, {});
    const passedCount = selectedStatusCounts.passed || 0;
    const warningCount = selectedStatusCounts.warning || 0;
    const failedCountExcludingErrors = selectedStatusCounts.failed || 0;
    const errorCount = selectedStatusCounts.error || 0;
    const inProgressCount = selectedStatusCounts[CHECK_IN_PROGRESS] || 0;
    const totalCount = selectedScanResults.length;

    // Compute failed requirements for latest scan (used for alert)
    const latestStatusCounts = reportedLatestResults.reduce((counts, result) => {
        const status = getScanResultStatus(result);
        counts[status] = (counts[status] || 0) + 1;
        return counts;
    }, {});
    const latestScanFailedCount = latestStatusCounts.failed || 0;
    const latestScanErrorCount = latestStatusCounts.error || 0;
    const latestScanTotalCount = latestScanResults.length;
    const hasFailedRequirements = latestScanFailedCount + latestScanErrorCount > 0;

    const lastRelevantScan = useMemo(() => findLastRelevantScan(scans), [scans]);

    const scanLimitReached = useMemo(
        () =>
            isScanWithinFrequencyLimit(
                lastRelevantScan?.completedAt || lastRelevantScan?.created,
                props.scanFrequencyDays
            ),
        [lastRelevantScan, props.scanFrequencyDays]
    );

    // Check if alert was dismissed for the latest scan
    // Guard against undefined to prevent false matches during loading states
    const alertDismissed = Boolean(latestScan?.id) && dismissedAlertScanIds.has(latestScan.id);

    // Get scan frequency label from scanIntervalDays
    // null means 'none', undefined means use default (weekly)
    const scanFrequency = useMemo(() => {
        const intervalDays =
            details?.scanIntervalDays === null
                ? 0
                : details?.scanIntervalDays ?? DEFAULT_SCAN_INTERVAL_DAYS;
        return FREQUENCY_MAP[intervalDays] || 'weekly';
    }, [details]);

    // Compute next scheduled scan date
    const nextScheduledScan = useMemo(() => {
        // No next scan when frequency is 'none' (null or 0)
        if (details?.scanIntervalDays === null || details?.scanIntervalDays === 0) {
            return null;
        }

        // Use API value if available
        if (details?.nextScheduledScan) {
            const date = moment(details.nextScheduledScan);
            return date.isValid() ? date : null;
        }

        // Otherwise compute from latest scan + interval
        const lastScanDate = latestScan?.completedAt || latestScan?.created;
        if (!lastScanDate) {
            return null;
        }

        const intervalDays = details?.scanIntervalDays ?? DEFAULT_SCAN_INTERVAL_DAYS;
        const date = moment(lastScanDate).add(intervalDays, 'days');
        return date.isValid() ? date : null;
    }, [details, latestScan]);

    return {
        alertDismissed,
        allResultsCacheHit,
        configId,
        details,
        domain,
        errorCount,
        failedCountExcludingErrors,
        awaitingFirstResults,
        selectedScanHasPendingChecks,
        handleDismissAlert,
        handleScanChange,
        handleStartScan,
        hasFailedRequirements,
        latestScan,
        latestScanErrorCount,
        latestScanFailedCount,
        latestScanTotalCount,
        loading,
        nextScheduledScan,
        inProgressCount,
        passedCount,
        scanChanging,
        scanDetail,
        scanDetailLoading,
        scanFrequency,
        scanInProgress,
        scanLimitReached,
        scans,
        selectedScan,
        selectedScanResults,
        totalCount,
        updateDetails,
        warningCount,
    };
};

export default useDomainDetails;
